// src/components/pages/OverviewPage.jsx
import React from 'react';
import { useSocketContext } from '../../context/SocketContext';

// Common
import StatCard from '../common/StatCard';

// Overview-specific
import RunningSyncCard from '../overview/RunningSyncCard';
import SyncFlowDiagram from '../overview/SyncFlowDiagram';
import RecentHistory from '../overview/RecentHistory';
import QuickSyncOverview from '../overview/QuickSyncOverview';

// Styles
import '../../styles/overview.css';
import '../../styles/schedule.css';

/* ── Helpers ──────────────────────────────────────────────── */

/**
 * Returns true if the given ISO date string falls within today (local time).
 */
const isToday = (dateStr) => {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
};

/* ── Component ────────────────────────────────────────────── */

const OverviewPage = () => {
  const { runningSyncs, syncHistory, isConnected, loading } = useSocketContext();

  // Stat counts
  const activeCount = runningSyncs ? runningSyncs.length : 0;

  const completedToday = syncHistory
    ? syncHistory.filter(
        (s) => s.status === 'completed' && isToday(s.startTime)
      ).length
    : 0;

  const failedToday = syncHistory
    ? syncHistory.filter(
        (s) => s.status === 'failed' && isToday(s.startTime)
      ).length
    : 0;

  return (
    <div className="overview-page">
      {/* ── Welcome bar ──────────────────────────────────── */}
      <div className="overview-welcome">
        <h1 className="overview-welcome-title">Sync Monitor</h1>
        <p className="overview-welcome-sub">
          {loading
            ? 'Connecting to server…'
            : isConnected
            ? 'Live — data updates in real time'
            : 'Disconnected — data may be stale'}
        </p>
      </div>

      {/* ── Stat cards ───────────────────────────────────── */}
      <div className="stat-cards-row">
        <StatCard
          icon="bi-activity"
          label="Active Syncs"
          value={activeCount}
          color="var(--accent-green)"
          subtitle={activeCount === 1 ? '1 sync in progress' : `${activeCount} syncs in progress`}
          pulse={activeCount > 0}
        />
        <StatCard
          icon="bi-check-circle"
          label="Completed Today"
          value={completedToday}
          color="var(--accent-blue)"
          subtitle="Successful syncs since midnight"
        />
        <StatCard
          icon="bi-x-circle"
          label="Failed Today"
          value={failedToday}
          color="var(--accent-red)"
          subtitle="Failed syncs since midnight"
        />
      </div>

      {/* ── Quick Sync ────────────────────────────────────── */}
      <QuickSyncOverview />

      {/* ── Running syncs (60%) + Flow diagram (40%) ─────── */}
      <div className="overview-main-row">
        {/* Running syncs */}
        <div className="running-syncs-section">
          <div className="running-syncs-heading">
            <i
              className="bi bi-broadcast"
              style={{ color: 'var(--accent-green)' }}
              aria-hidden="true"
            />
            Running Syncs
            {activeCount > 0 && (
              <span
                className="stat-pulse-dot"
                style={{
                  backgroundColor: 'var(--accent-green)',
                  marginLeft: 4,
                }}
                aria-hidden="true"
              />
            )}
          </div>

          {activeCount === 0 ? (
            <div className="running-syncs-empty">
              <i className="bi bi-moon-stars" aria-hidden="true" />
              No syncs currently running
            </div>
          ) : (
            runningSyncs.map((sync) => (
              <RunningSyncCard key={sync.id} sync={sync} />
            ))
          )}
        </div>

        {/* Flow diagram */}
        <SyncFlowDiagram runningSyncs={runningSyncs} />
      </div>

      {/* ── Recent history ───────────────────────────────── */}
      <RecentHistory syncHistory={syncHistory} />
    </div>
  );
};

export default OverviewPage;
